/**
 * API Documentation Service - Client-side service for fetching API documentation data
 * 
 * This service fetches field definitions and documentation data from the server
 * without directly accessing server-side repositories or file system operations
 */

import { apiFieldDefinitionLoader } from './apiFieldDefinitionLoader';
import { authService } from './authService';
import type { ApiFieldDefinition } from '../models/apiTypes';

export interface ApiDocField {
    name: string;
    apiFieldName: string;
    type: string;
    required: boolean;
    description: string;
    example?: string;
    units?: string;
    options?: string[];
    maxLength?: number;
}

class ApiDocumentationService {
    private baseUrl = '/api/v1';

    /**
     * Fetch field definitions from the server
     */
    async fetchFieldDefinitions(): Promise<ApiDocField[]> {
        try {
            // Get Firebase ID token for authentication
            const idToken = await authService.getIdToken();
            if (!idToken) {
                throw new Error('No authentication token available');
            }

            // Try to fetch from admin API first
            const response = await fetch(`${this.baseUrl}/admin/field-definitions?published=true&visible=true`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    console.log(`📚 API DOCS: Loaded ${data.data.length} visible field definitions from server`);
                    return this.convertServerFieldsToDocFields(data.data);
                }
            }
        } catch (error) {
            console.warn('📚 API DOCS: Failed to fetch field definitions from server:', error);
        }

        // Load from field definition loader with visibility filtering
        console.log('📚 API DOCS: Loading field definitions from definition loader');
        try {
            const allDefinitions = await apiFieldDefinitionLoader.loadFieldDefinitionsFromFile();
            const visibleDefinitions = apiFieldDefinitionLoader.getVisibleFields(allDefinitions);
            console.log(`📚 API DOCS: Loaded ${visibleDefinitions.length} visible fields from ${allDefinitions.length} total definitions`);
            return this.convertServerFieldsToDocFields(visibleDefinitions);
        } catch (error) {
            console.warn('📚 API DOCS: Failed to load from definition loader, using fallback:', error);
            return this.getFallbackFieldDefinitions();
        }
    }

    /**
     * Convert server field definitions to documentation format
     */
    private convertServerFieldsToDocFields(serverFields: ApiFieldDefinition[]): ApiDocField[] {
        const convertedFields = serverFields
            .filter(def => def.publish && def.visible && def.isApproved)
            .map((def): ApiDocField => ({
                name: def.displayName || def.fieldName,
                apiFieldName: def.apiFieldName,
                type: def.dataType || 'string',
                required: def.required || false,
                description: def.description || `${def.fieldName} field for sail specifications`,
                example: def.exampleValue || undefined,
                units: def.units || undefined,
                options: def.options || undefined,
                maxLength: def.maxLength
            }));

        // Sort with required fields first
        return convertedFields.sort((a, b) => {
            if (a.required && !b.required) return -1;
            if (!a.required && b.required) return 1;
            return a.name.localeCompare(b.name);
        });
    }

    /**
     * Fallback field definitions when server is unavailable
     */
    private getFallbackFieldDefinitions(): ApiDocField[] {
        return [
            // Required fields first
            {
                name: 'Product Number',
                apiFieldName: 'ProductNumber',
                type: 'string',
                required: true,
                description: 'Unique identifier for the sail product. This serves as the primary reference for all subsequent API operations.',
                example: 'MAIN-2024-BENETEAU-473-001'
            },
            {
                name: 'Request ID',
                apiFieldName: 'RequestId', 
                type: 'string',
                required: true,
                description: 'Unique identifier for this specific quote request. Must be unique across all requests.',
                example: 'REQ-20241225-14:30:25-001'
            },
            // Optional fields - boat specifications
            {
                name: 'Boat Model',
                apiFieldName: 'BoatModel',
                type: 'string',
                required: false,
                description: 'Make and model of the boat for which the sail is being quoted.',
                example: 'Beneteau Oceanis 473'
            },
            {
                name: 'Luff Length (Meters)',
                apiFieldName: 'LuffM',
                type: 'decimal',
                required: false,
                description: 'Length of the luff edge of the sail in meters. Critical for sail sizing and pricing.',
                example: '15.24',
                units: 'meters'
            },
            {
                name: 'Luff Length (Feet)',
                apiFieldName: 'LuffFt',
                type: 'decimal',
                required: false,
                description: 'Length of the luff edge of the sail in feet. Alternative to luff meters.',
                example: '50.0',
                units: 'feet'
            },
            {
                name: 'Leech Length (Meters)',
                apiFieldName: 'LeechM',
                type: 'decimal',
                required: false,
                description: 'Length of the leech edge of the sail in meters.',
                example: '14.46',
                units: 'meters'
            },
            {
                name: 'Foot Length (Meters)',
                apiFieldName: 'FootM',
                type: 'decimal',
                required: false,
                description: 'Length of the foot edge of the sail in meters.',
                example: '5.18',
                units: 'meters'
            },
            // Sail specifications
            {
                name: 'Full Battens',
                apiFieldName: 'FullBattens',
                type: 'integer',
                required: false,
                description: 'Number of full-length battens in the sail (0-10). Affects sail shape and price.',
                example: '4',
                options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
            },
            {
                name: 'Cloth Type',
                apiFieldName: 'ClothType',
                type: 'string',
                required: false,
                description: 'Primary sail cloth material specification.',
                example: 'Dacron 6.5oz'
            },
            {
                name: 'Units',
                apiFieldName: 'Units',
                type: 'string',
                required: false,
                description: 'Unit system used for measurements.',
                example: 'Meters',
                options: ['Meters', 'Feet']
            },
            // Hardware and construction options
            {
                name: 'Head Corner',
                apiFieldName: 'HeadCorner',
                type: 'string',
                required: false,
                description: 'Head corner construction specification including webbing and chafe protection.',
                example: 'Webbing Loops / 50mm Long / 25mm Wide / No Chafe Protection'
            },
            {
                name: 'Tack Corner',
                apiFieldName: 'TackCorner',
                type: 'string',
                required: false,
                description: 'Tack corner construction specification.',
                example: 'Standard Webbing Loop'
            },
            {
                name: 'Clew Corner',
                apiFieldName: 'ClewCorner',
                type: 'string',
                required: false,
                description: 'Clew corner construction specification.',
                example: 'Standard Webbing Loop'
            },
            {
                name: 'Luff Configuration',
                apiFieldName: 'LuffConfiguration',
                type: 'string',
                required: false,
                description: 'How the sail attaches to the mast.',
                example: 'Luff Rope',
                options: ['Luff Rope', 'Slides', 'Track System']
            },
            {
                name: 'Foot Configuration',
                apiFieldName: 'FootConfiguration',
                type: 'string',
                required: false,
                description: 'How the sail attaches to the boom.',
                example: 'Loose',
                options: ['Loose', 'Rope', 'Track']
            },
            // Additional specifications
            {
                name: 'Design Notes',
                apiFieldName: 'DesignNotes',
                type: 'text',
                required: false,
                description: 'Internal design notes and special requirements for the sail.',
                example: 'Customer requests reinforced corners for heavy weather use',
                maxLength: 1500
            },
            {
                name: 'Additional Notes',
                apiFieldName: 'AdditionalNotes',
                type: 'text',
                required: false,
                description: 'Additional customer requirements or special instructions.',
                example: 'Rush order - needed by March 15th'
            },
            // Pricing fields
            {
                name: 'Price per Unit',
                apiFieldName: 'PricePerUnit',
                type: 'decimal',
                required: false,
                description: 'Base price per unit before options and labor.',
                example: '2450.00',
                units: 'USD'
            },
            {
                name: 'Quantity',
                apiFieldName: 'Quantity',
                type: 'integer',
                required: false,
                description: 'Number of sails being ordered.',
                example: '1'
            },
            {
                name: 'Labour Quantity',
                apiFieldName: 'LabourQuantity',
                type: 'integer',
                required: false,
                description: 'Total labor time required in minutes.',
                example: '480',
                units: 'minutes'
            }
        ];
    }

    /**
     * Check if documentation is loaded from live data or fallback
     */
    async isLiveData(): Promise<boolean> {
        try {
            // Get Firebase ID token for authentication
            const idToken = await authService.getIdToken();
            if (!idToken) {
                return false; // No token means no live data access
            }

            const response = await fetch(`${this.baseUrl}/admin/field-definitions?published=true`, {
                method: 'HEAD',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`,
                },
            });
            return response.ok;
        } catch {
            return false;
        }
    }
}

export const apiDocumentationService = new ApiDocumentationService();